import React from 'react';
import {
  MdConfirmationNumber,
  MdEditNote,
  MdIosShare,
  MdPeople
} from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import { Modal as ModalAnt } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import { useDeleteEvent } from '../../hooks';
import { SECTION_ID } from 'screens/events/EventDashboard/components/new/ManageGuestsSection';
import { useEventPageContext } from './context/EventPageContext';

const MANAGE_EVENT_OPTIONS = [
  {
    condition: () => true,
    id: 'button-get-tickets',
    onClick:
      ({ toggleCheckout }) =>
      () =>
        toggleCheckout(),
    label: 'events:actions.getTickets',
    icon: <MdConfirmationNumber size={24} />
  },
  {
    condition: (event) => event.isOrganizer,
    id: 'button-edit-event',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}?view=edit`);
      },
    label: 'events:actions.eventDetails',
    icon: <MdEditNote size={24} />
  },
  {
    condition: (event) => event.isOrganizer || event.isScanner,
    id: 'button-manage-guests',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}/dashboard#${SECTION_ID}`);
      },
    label: 'events:actions.manageGuests',
    icon: <MdPeople size={24} />
  },
  {
    condition: (event) => event.isOrganizer || event.isPhotographer,
    featureFlag: FLAGS.ENABLE_EVENT_BULK_PHOTO_UPLOAD,
    id: 'button-upload-moments',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}?view=upload-moments`);
      },
    label: 'events:actions.uploadMoments',
    icon: (
      <img src="/img/events/create/moments.svg" className="w-[24px] h-[24px]" />
    )
  }
];

const ManageEvent: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const {
    event,
    toggleShareModal,
    toggleManageEvent,
    isManageEventModalOpen,
    canShare,
    toggleCheckout
  } = useEventPageContext();
  const router = useRouter();
  const deleteEventCommand = useDeleteEvent();
  const flags = useFlags();

  const onOptionClick = (action) => () => {
    toggleManageEvent();
    action();
  };

  const onDeleteDraft = () => {
    toggleManageEvent();

    ModalAnt.confirm({
      title: t('events:actions.deleteEvent'),
      icon: <ExclamationCircleOutlined />,
      content: event.title
        ? t('events:manageEvent.deleteEventConfirmationWithTitle', {
            title: event.title
          })
        : t('events:manageEvent.deleteEventConfirmation'),
      centered: true,
      okText: t('common:buttons.yes'),
      okType: 'danger',
      cancelText: t('common:buttons.no'),
      onOk() {
        deleteEventCommand.mutate(event._id, {
          onSuccess: () => {
            router.push('/host');
          }
        });
      }
    });
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={toggleManageEvent}
      autoHeight
      hideCloseButton
      visible={isManageEventModalOpen}
    >
      <div className="p-4 flex flex-col">
        <ul>
          {MANAGE_EVENT_OPTIONS.map((option) =>
            option.condition(event) &&
            (option.featureFlag ? flags[option.featureFlag] : true) ? (
              <li
                key={option.id}
                data-testid={option.id}
                className="font-normal text-base cursor-pointer py-4 flex align-center gap-2 text-neutral-600"
                onClick={onOptionClick(
                  option.onClick({
                    eventId: event._id,
                    router,
                    toggleCheckout
                  })
                )}
              >
                {option.icon}
                {t(option.label)}
              </li>
            ) : null
          )}
        </ul>
        <Divider size={4} />
        {canShare && (
          <div className="flex flex-col gap-2 mt-6">
            <Button
              type={ButtonType.Inverted}
              onClick={onOptionClick(toggleShareModal)}
              icon={<MdIosShare size={20} />}
            >
              {t('events:actions.shareEvent')}
            </Button>
          </div>
        )}
        {event.isOrganizer && (
          <>
            <Divider size={6} />
            <div className="mt-6">
              <Button
                type={ButtonType.Danger}
                icon={<IoMdTrash size={20} />}
                onClick={onDeleteDraft}
              >
                {t('events:actions.deleteEvent')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ManageEvent;
